import axios, { AxiosResponse } from "axios";
import { attachRateLimitRetry } from "./withRateLimitRetry";

// Types and Interfaces
export enum TicketStatus {
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  RESOLVED = "RESOLVED",
  CLOSED = "CLOSED",
}

export enum TicketType {
  TECHNICAL = "TECHNICAL",
  ACCOUNT = "ACCOUNT",
  BILLING = "BILLING",
  GENERAL = "GENERAL",
}

export enum TicketSubtype {
  LOGIN_ISSUE = "LOGIN_ISSUE",
  VIDEO_PLAYBACK = "VIDEO_PLAYBACK",
  DOWNLOAD_PROBLEM = "DOWNLOAD_PROBLEM",
  QUIZ_ERROR = "QUIZ_ERROR",
  MOBILE_APP = "MOBILE_APP",
  PROFILE_UPDATE = "PROFILE_UPDATE",
  PASSWORD_RESET = "PASSWORD_RESET",
  EMAIL_CHANGE = "EMAIL_CHANGE",
  CERTIFICATE_ISSUE = "CERTIFICATE_ISSUE",
  MISSING_CONTENT = "MISSING_CONTENT",
  CONTENT_ERROR = "CONTENT_ERROR",
  QUIZ_QUESTION = "QUIZ_QUESTION",
  COURSE_ACCESS = "COURSE_ACCESS",
  BILLING = "BILLING",
  FEEDBACK = "FEEDBACK",
  SUGGESTION = "SUGGESTION",
  OTHER = "OTHER",
}
export interface CreateTicketDto {
  title: string;
  description: string;
  type: TicketType;
  subtype: TicketSubtype;
  extraInfo?: string;
}

export interface UpdateTicketStatusDto {
  status: TicketStatus;
  statusUpdate?: string; // Max 200 chars
}

export interface SupportTicket {
  id: string;
  title: string;
  description: string;
  type: TicketType;
  subtype: TicketSubtype;
  extraInfo?: string;
  status: TicketStatus;
  statusUpdate?: string;

  // User info
  userId: string;
  user: {
    name?: string;
    email: string;
    indianCdc: string;
    rankDesignation?: string;
  };

  // Resolution info
  resolvedBy?: string;
  resolvedByUser?: {
    name?: string;
    email: string;
  };

  // Timestamps
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
  resolvedAt?: string; // ISO string
}

export interface TicketFilters {
  status?: TicketStatus;
  type?: TicketType;
  subtype?: TicketSubtype;
  userId?: string;
  dateFrom?: string; // ISO string
  dateTo?: string; // ISO string
  search?: string;
  page?: number;
  limit?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  timestamp: string;
}

export interface PaginatedResponse<T> extends ApiResponse<T> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  summary?: Record<TicketStatus, number>;
}

export interface UserTicketsResponse extends ApiResponse<SupportTicket[]> {
  counts: Record<TicketStatus, number>;
}

// API Base Configuration
const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8080";

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Add auth token interceptor
apiClient.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
// Registered first so it sees the raw AxiosError (with response.status
// and Retry-After) before the mapper below reshapes it.
attachRateLimitRetry(apiClient);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - redirect to login
      localStorage.removeItem("authToken");
      sessionStorage.removeItem("authToken");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// STUDENT/SEAFARER API FUNCTIONS
// ============================================================================

/**
 * Create a new support ticket
 */
export const createTicket = async (
  data: CreateTicketDto
): Promise<SupportTicket> => {
  const response: AxiosResponse<ApiResponse<SupportTicket>> =
    await apiClient.post("/api/support/tickets", data);
  return response.data.data;
};

/**
 * Get current user's tickets with optional status filter
 */
export const getMyTickets = async (
  status?: TicketStatus
): Promise<{
  tickets: SupportTicket[];
  counts: Record<TicketStatus, number>;
}> => {
  const params = status ? { status } : {};
  const response: AxiosResponse<UserTicketsResponse> = await apiClient.get(
    "/api/support/tickets/my",
    { params }
  );
  return {
    tickets: response.data.data,
    counts: response.data.counts,
  };
};

/**
 * Get a specific ticket by ID (user's own ticket)
 */
export const getMyTicket = async (ticketId: string): Promise<SupportTicket> => {
  const response: AxiosResponse<ApiResponse<SupportTicket>> =
    await apiClient.get(`/api/support/tickets/my/${ticketId}`);
  return response.data.data;
};

/**
 * Delete a user's own ticket
 */
export const deleteMyTicket = async (ticketId: string): Promise<void> => {
  await apiClient.delete(`/api/support/tickets/my/${ticketId}`);
};

/**
 * Reopen a resolved ticket (user can reopen their own tickets)
 */
export const reopenTicket = async (
  ticketId: string
): Promise<SupportTicket> => {
  const response: AxiosResponse<ApiResponse<SupportTicket>> =
    await apiClient.put(`/api/support/admin/tickets/${ticketId}/status`, {
      status: TicketStatus.PENDING,
    });
  return response.data.data;
};

// ============================================================================
// ADMIN API FUNCTIONS
// ============================================================================

/**
 * Get all tickets with filters (Admin only - Level 2+)
 */
export const getAllTickets = async (
  filters: TicketFilters = {}
): Promise<{
  tickets: SupportTicket[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  summary: Record<TicketStatus, number>;
}> => {
  // Clean up filters - remove undefined values
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined)
  );

  const response: AxiosResponse<PaginatedResponse<SupportTicket[]>> =
    await apiClient.get("/api/support/admin/tickets", { params: cleanFilters });

  return {
    tickets: response.data.data,
    pagination: response.data.pagination,
    summary: response.data.summary || ({} as Record<TicketStatus, number>),
  };
};

/**
 * Get any ticket by ID (Admin only)
 */
export const getTicketByIdAdmin = async (
  ticketId: string
): Promise<SupportTicket> => {
  const response: AxiosResponse<ApiResponse<SupportTicket>> =
    await apiClient.get(`/api/support/admin/tickets/${ticketId}`);
  return response.data.data;
};

/**
 * Update ticket status (Admin only)
 */
export const updateTicketStatus = async (
  ticketId: string,
  data: UpdateTicketStatusDto
): Promise<SupportTicket> => {
  const response: AxiosResponse<ApiResponse<SupportTicket>> =
    await apiClient.put(`/api/support/admin/tickets/${ticketId}/status`, data);
  return response.data.data;
};

export interface UpdateTicketCategoryDto {
  type: TicketType;
  subtype: TicketSubtype;
}

/**
 * Update ticket category / subtype (Admin only)
 */
export const updateTicketCategory = async (
  ticketId: string,
  data: UpdateTicketCategoryDto
): Promise<SupportTicket> => {
  const response: AxiosResponse<ApiResponse<SupportTicket>> =
    await apiClient.put(
      `/api/support/admin/tickets/${ticketId}/category`,
      data
    );
  return response.data.data;
};

/** Subtypes per category — keep in sync with seafarer create form + backend. */
export const TICKET_SUBTYPES_BY_TYPE: Record<
  TicketType,
  { value: TicketSubtype; label: string }[]
> = {
  [TicketType.TECHNICAL]: [
    { value: TicketSubtype.VIDEO_PLAYBACK, label: "Video Playback Issue" },
    { value: TicketSubtype.DOWNLOAD_PROBLEM, label: "Download Problem" },
    { value: TicketSubtype.QUIZ_ERROR, label: "Quiz Error" },
    { value: TicketSubtype.MISSING_CONTENT, label: "Missing Content" },
    { value: TicketSubtype.CONTENT_ERROR, label: "Content Error" },
    { value: TicketSubtype.CERTIFICATE_ISSUE, label: "Certificate Issue" },
  ],
  [TicketType.ACCOUNT]: [
    { value: TicketSubtype.LOGIN_ISSUE, label: "Login Issue" },
    { value: TicketSubtype.PASSWORD_RESET, label: "Password Reset" },
    { value: TicketSubtype.EMAIL_CHANGE, label: "Email Change" },
    { value: TicketSubtype.PROFILE_UPDATE, label: "Profile Update" },
  ],
  // Kept for historical tickets; not offered on create.
  [TicketType.BILLING]: [],
  [TicketType.GENERAL]: [
    { value: TicketSubtype.QUIZ_QUESTION, label: "Quiz Question" },
    { value: TicketSubtype.COURSE_ACCESS, label: "Course Access" },
    { value: TicketSubtype.FEEDBACK, label: "Feedback" },
    { value: TicketSubtype.SUGGESTION, label: "Suggestion" },
    { value: TicketSubtype.OTHER, label: "Other" },
  ],
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get human-readable status label
 */
export const getStatusLabel = (status: TicketStatus): string => {
  const labels = {
    [TicketStatus.PENDING]: "Pending Review",
    [TicketStatus.IN_PROGRESS]: "In Progress",
    [TicketStatus.RESOLVED]: "Resolved",
    [TicketStatus.CLOSED]: "Closed",
  };
  return labels[status] || status;
};

/**
 * Get status color for UI
 */
export const getStatusColor = (status: TicketStatus): string => {
  const colors = {
    [TicketStatus.PENDING]: "#f59e0b", // Amber
    [TicketStatus.IN_PROGRESS]: "#3b82f6", // Blue
    [TicketStatus.RESOLVED]: "#10b981", // Green
    [TicketStatus.CLOSED]: "#6b7280", // Gray
  };
  return colors[status] || "#6b7280";
};

/**
 * Get human-readable type label
 */
export const getTypeLabel = (type: TicketType): string => {
  const labels = {
    [TicketType.TECHNICAL]: "Technical Issue",
    [TicketType.ACCOUNT]: "Account Issue",
    [TicketType.BILLING]: "Billing Issue",
    [TicketType.GENERAL]: "General Inquiry",
  };
  return labels[type] || type;
};

/**
 * Get human-readable subtype label
 */
export const getSubtypeLabel = (subtype: TicketSubtype): string => {
  const labels: Record<TicketSubtype, string> = {
    [TicketSubtype.LOGIN_ISSUE]: "Login Issue",
    [TicketSubtype.VIDEO_PLAYBACK]: "Video Playback Issue",
    [TicketSubtype.DOWNLOAD_PROBLEM]: "Download Problem",
    [TicketSubtype.QUIZ_ERROR]: "Quiz Error",
    [TicketSubtype.MOBILE_APP]: "Mobile App Issue",
    [TicketSubtype.PROFILE_UPDATE]: "Profile Update",
    [TicketSubtype.PASSWORD_RESET]: "Password Reset",
    [TicketSubtype.EMAIL_CHANGE]: "Email Change",
    [TicketSubtype.CERTIFICATE_ISSUE]: "Certificate Issue",
    [TicketSubtype.MISSING_CONTENT]: "Missing Content",
    [TicketSubtype.CONTENT_ERROR]: "Content Error",
    [TicketSubtype.QUIZ_QUESTION]: "Quiz Question",
    [TicketSubtype.COURSE_ACCESS]: "Course Access",
    [TicketSubtype.BILLING]: "Billing Problem",
    [TicketSubtype.FEEDBACK]: "Feedback",
    [TicketSubtype.SUGGESTION]: "Suggestion",
    [TicketSubtype.OTHER]: "Other",
  };

  return labels[subtype] || subtype;
};

/**
 * Format date for display
 */
export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Get relative time string (e.g., "2 hours ago")
 */
export const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "Just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? "" : "s"} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
  }

  return formatDate(dateString);
};

/**
 * Validate ticket creation data
 */
export const validateCreateTicket = (data: CreateTicketDto): string[] => {
  const errors: string[] = [];

  if (!data.title?.trim()) {
    errors.push("Title is required");
  } else if (data.title.length > 100) {
    errors.push("Title must be 100 characters or less");
  }

  if (!data.description?.trim()) {
    errors.push("Description is required");
  } else if (data.description.length > 1000) {
    errors.push("Description must be 1000 characters or less");
  }

  if (!data.type) {
    errors.push("Ticket type is required");
  }

  if (!data.subtype) {
    errors.push("Ticket subtype is required");
  }

  if (data.extraInfo && data.extraInfo.length > 500) {
    errors.push("Extra information must be 500 characters or less");
  }

  return errors;
};

/**
 * Validate status update data
 */
export const validateStatusUpdate = (data: UpdateTicketStatusDto): string[] => {
  const errors: string[] = [];

  if (!data.status) {
    errors.push("Status is required");
  }

  if (data.statusUpdate && data.statusUpdate.length > 200) {
    errors.push("Status update must be 200 characters or less");
  }

  return errors;
};

// Export the configured axios instance for custom requests
export { apiClient };
